<template>
	<div :class="tabsClass" :data-component-id="_uid" class="tabs">
		<nav v-if="showNav" :class="navClass">
			<ul>
				<li
					v-for="(tab, index) in tabs"
					:key="index"
					:class="tabClass(tab)"
					:data-tab-status="tabStatus(tab)">
					<a
						:data-tab-status="tabStatus(tab)"
						:href="tab.href"
						v-html="tab.label"
						@click.prevent="selectTab(tab)"
					></a>
				</li>
				<div :style="activeTabIndicatorStyles" class="tabs__nav__active-indicator">
					<div></div>
				</div>
			</ul>
		</nav>
		<div :class="containerClass" class="tabs__container">
			<slot
				:goToNext="goToNext" :goToPrevious="goToPrevious" :goToTab="goToTab" :next="next"
				:nextIs="nextIs" :previous="previous"/>
		</div>
	</div>
</template>
<script>

import Stringer from "@/packages/stringer/src/Stringer";

export default {
	data() {
		return {
			tabs: [],
			selectedTabId: null,
			selectedTabElement: null,
			initialSelection: true,
		};
	},

	props: {
		disableNav: {
			required: false,
			type: Boolean,
			default() {
				return false;
			},
		},
		tabsClass: {
			required: false,
			type: String,
		},
		navClass: {
			required: false,
			type: String,
		},
		containerClass: {
			required: false,
			type: String,
		},
		tabClasses: {
			required: false,
			type: Object,
			default() {
				return {};
			},
		},
		tabless: {
			default: false,
		},
		hideNavigationForSingleTab: {
			default: true,
		},
		selected: {
			default: false,
		},
		updateHash: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
		mountOnShow: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
		mountOnce: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
		resetQuery: {
			type: Boolean,
			required: false,
			default() {
				return false;
			},
		},
	},

	computed: {
		activeTabIndicatorStyles() {
			let $el = this.selectedTabElement;
			let left = $el?.offsetLeft || 0;
			let width = $el?.offsetWidth || 0;
			return {
				left: left + "px",
				width: width + "px",
			};
		},
		showNav() {
			if (
				this.tabless ||
				!this.tabs.length
			) {
				return false;
			}

			if (this.tabs.length === 1 && this.hideNavigationForSingleTab) {
				return false;
			}

			return true;
		},
		previous() {
			let selectedTabIndex = _.indexOf(this.tabs, this.selectedTab);

			if (selectedTabIndex === 0) {
				return null;
			}

			return this.tabs[selectedTabIndex - 1];
		},
		next() {
			let selectedTabIndex = _.indexOf(this.tabs, this.selectedTab);

			if (selectedTabIndex === this.tabs.length - 1) {
				return null;
			}

			return this.tabs[selectedTabIndex + 1];
		},
		selectedTab() {
			if (!this.tabs.length) {
				return null;
			}

			if (!this.selectedTabId) {
				return null;
			}

			return _.find(
				this.tabs,
				function (tab) {
					return tab.indexedId === this.selectedTabId;
				}.bind(this),
			);
		},
		stepTabs() {
			if (!this.tabs.length) {
				return [];
			}

			return _.map(
				_.filter(this.tabs, function (tab) {
					return !tab.excludeFromTabSteps;
				}),
				function (tab) {
					return tab.tabIndex;
				},
			);
		},
		tabIsAStep() {
			if (!this.selectedTab) {
				return null;
			}

			return this.stepTabs.indexOf(this.selectedTab.tabIndex) > -1;
		},
		step() {
			if (!this.selectedTab || !this.tabIsAStep) {
				return null;
			}

			return this.stepTabs.indexOf(this.selectedTab.tabIndex) + 1;
		},
	},

	methods: {
		tabClass(tab) {
			return this.tabClasses[tab.indexedId] || {};
		},
		selectDefaultTab(always = false) {
			if (this.selectedTab && !always) {
				return;
			}

			let defaultSelectedTab = _.find(
				this.tabs,
				function (tab) {
					return !tab.excludeFromTabSteps;
				}.bind(this),
			);

			if (!defaultSelectedTab) {
				defaultSelectedTab = this.tabs[0];
			}

			if (!defaultSelectedTab) {
				return;
			}

			this.selectTab(defaultSelectedTab);
		},
		addTab(newTab) {
			let tabs = this.tabs;
			tabs.push(newTab);
			let tabIndex = this.tabIndex(newTab);
			this.tabs = _.orderBy(tabs, (tab) => {
				return tab.tabKey || tabIndex;
			}, "asc");
		},
		removeTab(tab) {
			this.tabs = _.orderBy(
				_.filter(this.tabs, function (_tab) {
					return _tab.indexedId !== tab.indexedId;
				}),
				(tab) => {
					return tab.tabKey || this.tabIndex(tab);
				},
				"asc",
			);
		},
		tabStatus(tab) {
			return tab.isActive ? "active" : "inactive";
		},
		tabIndex(tab) {
			return _.findIndex(this.tabs, ["indexedId", tab.indexedId]);
		},
		goToPrevious() {
			if (!this.previous) {
				return;
			}

			this.selectTab(this.previous);
		},
		goToNext() {
			if (!this.next) {
				return;
			}

			this.selectTab(this.next);
		},
		goToTab(id) {
			this.selectTab(this.getTabByHash(id));
		},
		selectTab(tab) {
			if (!tab) {
				return;
			}

			if (this.selectedTab?.indexedId === tab.indexedId) {
				return;
			}

			this.selectedTabId = tab.indexedId;

			tab.mount();
			tab.callOnShow();

			let currentUrl = new URL(window.location.href);
			let url = new URL(window.location.href);

			if (this.updateHash) {
				url.hash = "#" + tab.indexedId;
			}

			if (this.resetQuery) {
				url.search = "";
			}

			if (url.toString() !== currentUrl.toString()) {
				let route = Object.assign({}, this.$route);
				route.hash = url.hash;
				route.query = Qs.parse(new Stringer(url.search).lTrim("\\?").value) ?? {};
				this.$router.replace(route);
			}
		},
		normalizeHash(string) {
			return string.trim().replace(/^#/g, "");
		},
		getSelectedTab() {
			return this.selectedTab;
		},
		isTabSelected(tabId) {
			return this.selectedTabId === tabId;
		},
		getTotalTabSteps(tabs = null) {
			tabs = tabs || this.tabs;
			return _.filter(tabs, function (tab) {
				return !tab.excludeFromTabSteps;
			}).length;
		},
		getTabByHash(hash) {
			hash = this.normalizeHash(hash);

			let tab = _.find(this.tabs, function (tab) {
				return tab.id === hash;
			});
			return tab;
		},
		nextIs(id) {
			return this.next?.id === id;
		},
	},

	watch: {
		selectedTab() {
			if (!this.selectedTab) {
				return;
			}

			this.$nextTick(function () {
				this.selectedTabElement = this.$el.querySelector("nav li[data-tab-status=\"active\"]");
			});

			this.$emit("selectedTabChanged", {
				selectedTab: this.selectedTab,
				step: Math.min(this.step, this.getTotalTabSteps()),
				tabIsAStep: this.tabIsAStep,
				initialSelection: this.initialSelection,
			});

			this.initialSelection = false;

			let tabsElementSelector = `[data-component-id="${this._uid}"]`;

			this.scrollTo(tabsElementSelector, {
				whenAboveView: true,
				shift: 16,
			});

			this.$vmodal.scrollTo(tabsElementSelector);
		},
		tabs(newTabList, oldTabList) {
			this.selectedTabElement = null;
			this.$nextTick(function () {
				this.selectedTabElement = this.$el.querySelector("nav li[data-tab-status=\"active\"]");
			});

			this.$emit("tabsChanged", {
				tabs: newTabList,
				steps: this.stepTabs,
			});

			this.selectDefaultTab();
		},
		$route(to, from) {
			if (!to.hash) {
				return;
			}

			if (to.hash !== from.hash) {
				this.selectTab(this.getTabByHash(to.hash));
			}
		},
	},

	mounted() {
		let hash = window.location.hash;
		if (hash) {
			this.goToTab(hash);
		} else {
			this.selectDefaultTab(true);
		}
	},
};
</script>

